/**
 * Utilities.js — shared helper functions
 */

/**
 * Throttle: limit fn to fire at most once per `limit` ms.
 * Used for scroll / resize handlers to keep the main thread free.
 */
export const throttle = (fn, limit = 100) => {
  let lastCall = 0;
  return (...args) => {
    const now = performance.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      fn(...args);
    }
  };
};

/**
 * Debounce: delay fn until `delay` ms after the last call.
 */
export const debounce = (fn, delay = 200) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Query helper — throws if the element is missing (catches typos early).
 */
export const qs = (selector, scope = document) => {
  const el = scope.querySelector(selector);
  if (!el) throw new Error(`[OK] Element not found: "${selector}"`);
  return el;
};

export const qsa = (selector, scope = document) =>
  Array.from(scope.querySelectorAll(selector));

/**
 * Wait for DOM ready without a framework.
 */
export const onDOMReady = (fn) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
};

/**
 * Detect if the user prefers reduced motion.
 */
export const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Generate a stable numeric year for footer copyright.
 */
export const currentYear = () => new Date().getFullYear();
